import type { DspModuleDescriptor } from '../../dsp-module-contract/src/index.js';

export const MAX_MODULATION_ROUTES = 128;

/** Host-facing metadata for the control router. MOD-017 deliberately remains a
 * routing utility rather than pretending to be a DspModuleDefinition. */
export const mod017Descriptor: DspModuleDescriptor = Object.freeze({
  id: 'mir.mod-017',
  name: 'Modulation matrix',
  version: '1.0.0',
  inputs: Object.freeze([{ id: 'in', kind: 'control' as const, channels: 1 }]),
  outputs: Object.freeze([{ id: 'out', kind: 'control' as const, channels: 1 }]),
  parameters: Object.freeze([]),
});

export type ModulationTransform = 'identity' | 'absolute' | 'unipolar' | 'signed-square';

export interface ModulationSourcePort<SourceId extends string = string> {
  readonly id: SourceId;
  readonly kind: 'control';
}

export interface ModulationTargetPort<TargetId extends string = string> {
  readonly id: TargetId;
  readonly kind: 'parameter';
  readonly minimum: number;
  readonly maximum: number;
  readonly baseValue: number;
}

export interface ModulationRoute<SourceId extends string = string, TargetId extends string = string> {
  readonly id: string;
  readonly source: SourceId;
  readonly target: TargetId;
  /** Bipolar route amount. Negative values invert the transformed modulation. */
  readonly depth: number;
  readonly transform?: ModulationTransform;
  /** Linear depth-ramp duration used by setRouteDepth(). */
  readonly smoothingSeconds?: number;
}

export interface ModulationMatrixOptions<SourceId extends string, TargetId extends string> {
  readonly sampleRate: number;
  readonly sources: readonly ModulationSourcePort<SourceId>[];
  readonly targets: readonly ModulationTargetPort<TargetId>[];
  readonly routes: readonly ModulationRoute<SourceId, TargetId>[];
}

interface CompiledRoute {
  readonly id: string;
  readonly sourceIndex: number;
  readonly targetIndex: number;
  readonly transform: ModulationTransform;
  readonly initialDepth: number;
  readonly smoothingSamples: number;
  currentDepth: number;
  targetDepth: number;
  depthStep: number;
  rampSamplesRemaining: number;
}

const assertFinite = (label: string, value: number): void => {
  if (!Number.isFinite(value)) throw new RangeError(`${label} must be finite`);
};

const transformValue = (value: number, transform: ModulationTransform): number => {
  if (transform === 'absolute') return Math.abs(value);
  if (transform === 'unipolar') return (value + 1) * 0.5;
  if (transform === 'signed-square') return Math.sign(value) * value * value;
  return value;
};

/**
 * Fixed-capacity control-rate modulation aggregator.
 *
 * Construction compiles string ports to integer indices. processInto() only
 * traverses existing arrays and scalar state: it creates no arrays, objects,
 * closures, subarrays, or other per-block garbage.
 */
export class ModulationMatrix<SourceId extends string, TargetId extends string> {
  readonly sampleRate: number;

  private readonly sourceIds: readonly SourceId[];
  private readonly targetIds: readonly TargetId[];
  private readonly targetMinimums: Float64Array;
  private readonly targetMaximums: Float64Array;
  private readonly targetBases: Float64Array;
  private readonly routes: readonly CompiledRoute[];

  constructor(options: ModulationMatrixOptions<SourceId, TargetId>) {
    assertFinite('sampleRate', options.sampleRate);
    if (options.sampleRate <= 0 || options.sampleRate > 768_000) {
      throw new RangeError('sampleRate must be in (0, 768000]');
    }
    if (options.routes.length > MAX_MODULATION_ROUTES) {
      throw new RangeError(`modulation matrix supports at most ${MAX_MODULATION_ROUTES} routes`);
    }

    const sourceIds: SourceId[] = [];
    const sourceIndices = new Map<SourceId, number>();
    for (const source of options.sources) {
      if (source.kind !== 'control') throw new RangeError('source kind must be control');
      if (!source.id || sourceIndices.has(source.id)) throw new RangeError(`duplicate or empty source: ${source.id}`);
      sourceIndices.set(source.id, sourceIds.length);
      sourceIds.push(source.id);
    }

    const targetIds: TargetId[] = [];
    const targetIndices = new Map<TargetId, number>();
    const minimums = new Float64Array(options.targets.length);
    const maximums = new Float64Array(options.targets.length);
    const bases = new Float64Array(options.targets.length);
    for (const target of options.targets) {
      if (target.kind !== 'parameter') throw new RangeError('target kind must be parameter');
      if (!target.id || targetIndices.has(target.id)) throw new RangeError(`duplicate or empty target: ${target.id}`);
      assertFinite(`target ${target.id} minimum`, target.minimum);
      assertFinite(`target ${target.id} maximum`, target.maximum);
      assertFinite(`target ${target.id} baseValue`, target.baseValue);
      if (target.minimum > target.maximum || target.baseValue < target.minimum || target.baseValue > target.maximum) {
        throw new RangeError(`target ${target.id} bounds/baseValue are invalid`);
      }
      const index = targetIds.length;
      targetIndices.set(target.id, index);
      targetIds.push(target.id);
      minimums[index] = target.minimum;
      maximums[index] = target.maximum;
      bases[index] = target.baseValue;
    }

    const routeIds = new Set<string>();
    const routes: CompiledRoute[] = [];
    for (const route of options.routes) {
      if (!route.id || routeIds.has(route.id)) throw new RangeError(`duplicate or empty route: ${route.id}`);
      routeIds.add(route.id);
      const sourceIndex = sourceIndices.get(route.source);
      if (sourceIndex === undefined) throw new RangeError(`unknown source: ${route.source}`);
      const targetIndex = targetIndices.get(route.target);
      if (targetIndex === undefined) throw new RangeError(`unknown target: ${route.target}`);
      assertFinite(`route ${route.id} depth`, route.depth);
      if (route.depth < -1 || route.depth > 1) throw new RangeError(`route ${route.id} depth must be in [-1, 1]`);
      const smoothingSeconds = route.smoothingSeconds ?? 0;
      assertFinite(`route ${route.id} smoothingSeconds`, smoothingSeconds);
      if (smoothingSeconds < 0 || smoothingSeconds > 60) {
        throw new RangeError(`route ${route.id} smoothingSeconds must be in [0, 60]`);
      }
      const transform = route.transform ?? 'identity';
      if (transform !== 'identity' && transform !== 'absolute'
        && transform !== 'unipolar' && transform !== 'signed-square') {
        throw new RangeError(`route ${route.id} transform is invalid`);
      }
      routes.push({
        id: route.id,
        sourceIndex,
        targetIndex,
        transform,
        initialDepth: route.depth,
        smoothingSamples: Math.ceil(smoothingSeconds * options.sampleRate),
        currentDepth: route.depth,
        targetDepth: route.depth,
        depthStep: 0,
        rampSamplesRemaining: 0,
      });
    }

    this.sampleRate = options.sampleRate;
    this.sourceIds = sourceIds;
    this.targetIds = targetIds;
    this.targetMinimums = minimums;
    this.targetMaximums = maximums;
    this.targetBases = bases;
    this.routes = routes;
  }

  reset(): void {
    for (let index = 0; index < this.routes.length; index += 1) {
      const route = this.routes[index]!;
      route.currentDepth = route.initialDepth;
      route.targetDepth = route.initialDepth;
      route.depthStep = 0;
      route.rampSamplesRemaining = 0;
    }
  }

  setRouteDepth(routeId: string, depth: number): void {
    assertFinite('depth', depth);
    if (depth < -1 || depth > 1) throw new RangeError('depth must be in [-1, 1]');
    let route: CompiledRoute | undefined;
    for (let index = 0; index < this.routes.length; index += 1) {
      if (this.routes[index]!.id === routeId) {
        route = this.routes[index];
        break;
      }
    }
    if (!route) throw new RangeError(`unknown route: ${routeId}`);
    route.targetDepth = depth;
    route.rampSamplesRemaining = route.smoothingSamples;
    if (route.smoothingSamples === 0) {
      route.currentDepth = depth;
      route.depthStep = 0;
    } else {
      route.depthStep = (depth - route.currentDepth) / route.smoothingSamples;
    }
  }

  processInto(
    sources: Readonly<Record<SourceId, Float32Array>>,
    targets: Readonly<Record<TargetId, Float32Array>>,
  ): void {
    let frames = -1;
    for (let index = 0; index < this.targetIds.length; index += 1) {
      const buffer = targets[this.targetIds[index]!];
      if (!(buffer instanceof Float32Array)) throw new RangeError(`missing target buffer: ${this.targetIds[index]}`);
      if (frames < 0) frames = buffer.length;
      else if (buffer.length !== frames) throw new RangeError('all buffers must have the same frame length');
    }
    if (frames < 0) frames = 0;
    for (let index = 0; index < this.sourceIds.length; index += 1) {
      const buffer = sources[this.sourceIds[index]!];
      if (!(buffer instanceof Float32Array)) throw new RangeError(`missing source buffer: ${this.sourceIds[index]}`);
      if (buffer.length !== frames) throw new RangeError('all buffers must have the same frame length');
      for (let frame = 0; frame < frames; frame += 1) {
        if (!Number.isFinite(buffer[frame]!)) {
          throw new RangeError(`finite source value required for ${this.sourceIds[index]}`);
        }
        if (buffer[frame]! < -1 || buffer[frame]! > 1) {
          throw new RangeError(`normalized source value in [-1, 1] required for ${this.sourceIds[index]}`);
        }
      }
    }

    for (let frame = 0; frame < frames; frame += 1) {
      for (let targetIndex = 0; targetIndex < this.targetIds.length; targetIndex += 1) {
        targets[this.targetIds[targetIndex]!]![frame] = this.targetBases[targetIndex]!;
      }
      for (let routeIndex = 0; routeIndex < this.routes.length; routeIndex += 1) {
        const route = this.routes[routeIndex]!;
        if (route.rampSamplesRemaining > 0) {
          route.currentDepth += route.depthStep;
          route.rampSamplesRemaining -= 1;
          if (route.rampSamplesRemaining === 0) route.currentDepth = route.targetDepth;
        }
        const source = sources[this.sourceIds[route.sourceIndex]!]![frame]!;
        const target = targets[this.targetIds[route.targetIndex]!]!;
        target[frame] = target[frame]! + transformValue(source, route.transform) * route.currentDepth;
      }
      for (let targetIndex = 0; targetIndex < this.targetIds.length; targetIndex += 1) {
        const target = targets[this.targetIds[targetIndex]!]!;
        const value = target[frame]!;
        target[frame] = Math.max(this.targetMinimums[targetIndex]!, Math.min(this.targetMaximums[targetIndex]!, value));
      }
    }
  }
}